import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import Role from "@mapmycustomers/shared/enum/Role";
import SubscriptionInterval from "@mapmycustomers/shared/enum/SubscriptionInterval";
import CurrentPlan from "@mapmycustomers/shared/types/plans/CurrentPlan";
import Tier from "@mapmycustomers/shared/types/plans/Tier";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { getMe } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import localSettings from "../../config/LocalSettings";
import Path from "../../enum/Path";
import Iam from "../../types/Iam";
import useModalVisibilityAnalyticEffect from "../../util/hook/useModalVisibilityAnalyticEffect";

import styles from "./Billing.module.scss";
import billingAnalytics from "./billingAnalytics";
import BillingInformation from "./components/BillingInformation";
import CheckoutModal from "./components/Checkout/CheckoutModal";
import Footer from "./components/Footer";
import LicenseRow from "./components/LicenseRow";
import SummaryRows from "./components/SummaryRows";
import {
  getCurrentPlan,
  getLicenses,
  getSelectedTier,
  getStripe,
  getTiers,
  isCheckoutModalVisible,
  isLoading,
} from "./store";
import { hideCheckoutModal, initialize, showCheckoutModal } from "./store/actions";
import useSavingsInformation from "./util/useSavingsInformation";

interface Props {
  checkoutModalVisible: boolean;
  currentPlan?: CurrentPlan;
  initialize: () => void;
  licenses: number;
  loading: boolean;
  me: Iam;
  onHideCheckout: () => void;
  onShowCheckout: () => void;
  selectedTier?: Tier;
  stripe: Stripe | undefined;
  tiers: Tier[];
}

const BillingPage: React.FC<Props> = ({
  checkoutModalVisible,
  currentPlan,
  initialize,
  licenses,
  loading,
  me,
  onHideCheckout,
  onShowCheckout,
  selectedTier,
  stripe,
  tiers,
}) => {
  const intl = useIntl();
  useEffect(() => {
    initialize();
  }, [initialize]);

  const enterpriseLicenseLimit = useMemo(() => {
    const enterprisePlan = tiers.find(
      ({ licenseLimit }) => licenseLimit.length === 1 && licenseLimit[0] > 0
    );
    return enterprisePlan ? enterprisePlan.licenseLimit[0] : Number.POSITIVE_INFINITY;
  }, [tiers]);

  const history = useHistory();
  const handleCompleteCheckout = useCallback(() => {
    // was it opened from a mobile app?
    if (localSettings.wasOpenedFromMobileApp()) {
      window.location.href = "mapmycustomers://"; // open mobile app again...
      window.close(); // ...and try closing the page
    } else {
      history.push(Path.DASHBOARD); // otherwise go to home page
    }
  }, [history]);

  const savingsInformation = useSavingsInformation(currentPlan, selectedTier, tiers);
  useModalVisibilityAnalyticEffect("Proceed Checkout", checkoutModalVisible, billingAnalytics);

  if (loading || !stripe || !selectedTier || !currentPlan) {
    return <LoadingSpinner global />;
  }

  // We force users to talk to sales before going to the Enterprise plan
  // This condition works if their current plan is not Enterprise, but they added so many
  // licenses that it should become Enterprise.
  const forceTalkToSalesBeforehand =
    enterpriseLicenseLimit > currentPlan.licenses && enterpriseLicenseLimit <= licenses;

  // Checkout enabled if they added any new licenses OR switched from monthly to annual
  const checkoutEnabled =
    licenses > currentPlan.licenses ||
    (currentPlan.tier.interval === SubscriptionInterval.MONTHLY &&
      selectedTier.interval === SubscriptionInterval.ANNUAL);

  return (
    <Elements stripe={stripe}>
      <section className={styles.container}>
        <h1 className={styles.title}>
          {intl.formatMessage({
            id: "billing.title",
            defaultMessage: "Manage Your Subscription",
            description: "Billing page title",
          })}
        </h1>
        <section className={styles.content}>
          <BillingInformation currentPlan={currentPlan} savingsInformation={savingsInformation} />

          {me.role.key === Role.OWNER && (
            <div className={styles.gridContainer}>
              {currentPlan.isOldPlan && <div className={styles.teaserOverlay} />}

              <div className={styles.planGrid}>
                <LicenseRow
                  currentPlan={currentPlan}
                  savingsInformation={savingsInformation}
                  selectedTier={selectedTier}
                />

                <SummaryRows currentPlan={currentPlan} selectedTier={selectedTier} />
              </div>
            </div>
          )}
          {me.role.key === Role.OWNER && !currentPlan.isOldPlan && (
            <Footer
              checkoutEnabled={checkoutEnabled}
              hideCheckout={forceTalkToSalesBeforehand}
              onCheckoutClick={onShowCheckout}
            />
          )}
        </section>
      </section>
      {checkoutModalVisible && (
        <CheckoutModal
          onCompleteCheckout={handleCompleteCheckout}
          onHide={onHideCheckout}
          selectedTier={selectedTier}
        />
      )}
    </Elements>
  );
};

const mapStateToProps = (state: RootState) => ({
  checkoutModalVisible: isCheckoutModalVisible(state),
  currentPlan: getCurrentPlan(state),
  licenses: getLicenses(state),
  loading: isLoading(state),
  me: getMe(state)!, // profile must be loaded at this point
  selectedTier: getSelectedTier(state),
  stripe: getStripe(state),
  tiers: getTiers(state),
});

const mapDispatchToProps = {
  initialize: initialize.request,
  onHideCheckout: hideCheckoutModal,
  onShowCheckout: showCheckoutModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(BillingPage);
